import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowUpDown, ChevronDown, X } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  points_balance: number;
  customer_code: string | null;
  username: string | null;
  email: string | null;
}

interface ActiveVoucher {
  id: string;
  code: string | null;
  status: string;
  created_at: string | null;
  reward: { title: string; voucher_percent: number | null } | null;
}

// Solo due stati mostrati: Attivo / Usato
function statusInfo(status: string) {
  if (status === "used")
    return { label: "Usato", text: "text-[#9A958E]", bg: "bg-[#9A958E]/12", dot: "#9A958E" };
  return { label: "Attivo", text: "text-[#3a8a4f]", bg: "bg-[#3a8a4f]/12", dot: "#3a8a4f" };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CartaArredaDrawer({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [vouchers, setVouchers] = useState<ActiveVoucher[]>([]);

  useEffect(() => {
    if (!open || !user) return;
    let active = true;
    Promise.all([
      supabase
        .from("profiles")
        .select("points_balance, customer_code, username, email")
        .eq("id", user.id)
        .maybeSingle(),
      supabase
        .from("redemptions")
        .select("id, code, status, created_at, reward:rewards(title, voucher_percent)")
        .eq("user_id", user.id)
        .eq("type", "voucher")
        .in("status", ["requested", "approved", "used"])
        .order("created_at", { ascending: false }),
    ]).then(([profRes, vouchRes]) => {
      if (!active) return;
      if (profRes.data) setProfile(profRes.data as Profile);
      const list = (vouchRes.data ?? [])
        .filter((r: any) => r.reward && !Array.isArray(r.reward))
        .map((r: any) => ({ id: r.id, code: r.code, status: r.status, created_at: r.created_at, reward: r.reward })) as ActiveVoucher[];
      setVouchers(list);
    });
    return () => {
      active = false;
    };
  }, [open, user]);

  const [sortBy, setSortBy] = useState<"recent" | "discount">("recent");
  const [order, setOrder] = useState<"desc" | "asc">("desc");
  const [detail, setDetail] = useState<ActiveVoucher | null>(null);

  const sortedVouchers = useMemo(() => {
    const arr = [...vouchers];
    arr.sort((a, b) => {
      const cmp =
        sortBy === "discount"
          ? (a.reward?.voucher_percent ?? 0) - (b.reward?.voucher_percent ?? 0)
          : (a.created_at ?? "").localeCompare(b.created_at ?? "");
      return order === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [vouchers, sortBy, order]);

  const activeCount = vouchers.length;

  const customerCode =
    profile?.customer_code || (user ? `CL-${user.id.slice(0, 8).toUpperCase()}` : "");
  const name = profile?.username || profile?.email?.split("@")[0] || "Utente";

  return (
    <>
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="border-0 rounded-t-[20px] overflow-hidden bg-[#F6F4F1] h-[94dvh] flex flex-col">
        {/* Header band */}
        <div
          className="px-5 pt-5 pb-28 shrink-0"
          style={{ background: "linear-gradient(160deg, #B39678 0%, #86694B 100%)" }}
        >
          <button
            type="button"
            aria-label="Chiudi"
            onClick={() => onOpenChange(false)}
            onPointerDownCapture={(e) => e.stopPropagation()}
            className="-ml-2 -mt-1 mb-3 p-2 inline-flex items-center justify-center rounded-full active:bg-white/15 transition-colors"
          >
            <X className="w-7 h-7 text-white" strokeWidth={2} />
          </button>
          <DrawerTitle className="text-white text-[26px] font-bold tracking-tight leading-tight">
            La tua Carta Arreda
          </DrawerTitle>
          <DrawerDescription className="text-white/85 text-[14px] mt-1.5">
            Scansiona il QR code per godere di tutti i vantaggi
          </DrawerDescription>
        </div>

        {/* Card */}
        <div className="relative z-10 px-4 -mt-20 shrink-0">
          <div className="rounded-[16px] bg-white shadow-[0_14px_34px_-18px_rgba(0,0,0,0.4)] px-6 py-6">
            <div className="flex justify-center pt-1 pb-2">
              <QRCodeSVG
                value={customerCode}
                size={184}
                level="H"
                includeMargin={false}
                fgColor="#111111"
                bgColor="transparent"
              />
            </div>
            <p className="mt-5 text-[16px] font-bold text-[#111]">{name}</p>
            <p className="text-[15px] font-mono text-[#6A6661] tracking-[0.04em] mt-0.5">
              {customerCode}
            </p>
          </div>
        </div>

        {/* Intestazione + filtri (fissi) */}
        <div className="shrink-0 px-4 pt-6 pb-3 bg-[#F6F4F1]">
          <p className="text-[15px] font-bold text-[#111] mb-3">
            {activeCount === 1 ? "1 coupon" : `${activeCount} coupon`}
          </p>

          {activeCount > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
                  aria-label="Inverti ordine"
                  className="flex-shrink-0 w-10 h-10 rounded-full border border-[#111]/15 bg-white flex items-center justify-center active:scale-95 transition-transform"
                >
                  <ArrowUpDown className="w-4 h-4 text-[#111]" />
                </button>
                <button
                  onClick={() => setSortBy("recent")}
                  className={`flex-shrink-0 flex items-center gap-1.5 h-10 px-4 rounded-full border text-[13.5px] font-medium active:scale-95 transition-transform ${
                    sortBy === "recent"
                      ? "border-[#86694B] bg-[#86694B] text-white"
                      : "border-[#111]/15 bg-white text-[#111]"
                  }`}
                >
                  {order === "desc" ? "Più recenti" : "Meno recenti"}
                  <ChevronDown className={`w-4 h-4 ${sortBy === "recent" ? "text-white/80" : "text-[#6A6661]"}`} />
                </button>
                <button
                  onClick={() => setSortBy("discount")}
                  className={`flex-shrink-0 flex items-center gap-1.5 h-10 px-4 rounded-full border text-[13.5px] font-medium active:scale-95 transition-transform ${
                    sortBy === "discount"
                      ? "border-[#86694B] bg-[#86694B] text-white"
                      : "border-[#111]/15 bg-white text-[#111]"
                  }`}
                >
                  Sconto
                  <ChevronDown className={`w-4 h-4 ${sortBy === "discount" ? "text-white/80" : "text-[#6A6661]"}`} />
                </button>
              </div>
          )}
        </div>

        {/* Lista scrollabile */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-8">
          {activeCount === 0 ? (
            <p className="text-[14px] text-[#9A958E]">Non hai ancora coupon.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sortedVouchers.map((v) => {
                const info = statusInfo(v.status);
                return (
                  <button
                    key={v.id}
                    onClick={() => setDetail(v)}
                    className="w-full text-left rounded-[16px] bg-white border border-[#111]/[0.05] p-4 shadow-[0_4px_24px_-16px_rgba(0,0,0,0.25)] flex items-center justify-between gap-3 active:scale-[0.99] transition-transform"
                  >
                    <p className="text-[16px] font-bold text-[#111] leading-snug">
                      {v.reward?.title || "Coupon"}
                    </p>
                    <span className={`shrink-0 text-[12px] font-semibold px-2.5 py-1 rounded-full ${info.bg} ${info.text}`}>
                      {info.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>

    {/* Dettaglio coupon */}
    <Drawer open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
      <DrawerContent className="border-0 rounded-t-[24px] bg-white">
        <div className="mx-auto mt-3 mb-1 h-1.5 w-10 rounded-full bg-[#E0DAD2]" />
        <div className="px-5 pt-3" style={{ paddingBottom: "calc(var(--safe-area-bottom, 0px) + 28px)" }}>
          <DrawerTitle className="text-[22px] font-bold text-[#111] tracking-tight">
            {detail?.reward?.title || "Coupon"}
          </DrawerTitle>
          <DrawerDescription className="sr-only">Dettaglio e stato del coupon</DrawerDescription>

          {detail && (
            <>
              <div className="mt-4 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: statusInfo(detail.status).dot }} />
                <span className={`text-[17px] font-bold ${statusInfo(detail.status).text}`}>
                  {statusInfo(detail.status).label}
                </span>
              </div>

              <div className="mt-5 rounded-[16px] bg-[#F4F1EC] p-4 space-y-3">
                {detail.reward?.voucher_percent != null && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13px] text-[#9A958E]">Sconto</span>
                    <span className="text-[14px] font-semibold text-[#111]">{detail.reward.voucher_percent}%</span>
                  </div>
                )}
                {detail.code && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13px] text-[#9A958E]">Codice</span>
                    <span className="text-[14px] font-mono font-semibold text-[#111] tracking-[0.04em]">{detail.code}</span>
                  </div>
                )}
                {detail.created_at && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13px] text-[#9A958E]">Attivato il</span>
                    <span className="text-[14px] font-semibold text-[#111]">
                      {new Date(detail.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                  </div>
                )}
              </div>

              <p className="text-[13.5px] text-[#6A6661] mt-4 leading-relaxed">
                {detail.status === "used"
                  ? "Questo coupon è già stato utilizzato e non è più valido."
                  : "Mostra questo codice alla cassa del nostro showroom per usare il coupon."}
              </p>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
    </>
  );
}
